import React, { useEffect, useState } from 'react';
import { api } from 'lib/api';
import { API_ENDPOINTS } from 'constants/endpoints';
import { usePopup } from 'context/PopupProvider';
import { useAuth } from 'context/AuthProvider';
import {
  ShieldCheck, UserPlus, RefreshCw, X, Shield, Users, Trash2, Pencil,
  Save, Plus, Lock, KeyRound, Check,
} from 'lucide-react';
import { ADMIN_STATUS } from 'constants/enums';
import { listRowNumber, LIST_TD_NO_CLASS, LIST_TH_NO_CLASS, LIST_TABLE_CLASS, LIST_THEAD_CLASS, LIST_TBODY_CLASS, LIST_TH_CLASS, LIST_TD_CLASS } from 'lib/list-table';

interface ModuleDef {
  key: string;
  name: string;
  description: string;
}

interface Role {
  id: number;
  name: string;
  description: string;
  permissions: string[];
  is_system: boolean;
  user_count: number | string;
}

interface AdminUser {
  id: number;
  username: string;
  email: string;
  // admin_users.status SMALLINT code: 1 = Active, 2 = Suspended
  status: number;
  role_id: number | null;
  role_name: string | null;
  permissions: string[] | null;
}

type Tab = 'users' | 'roles';

const emptyAdminForm = { username: '', email: '', password: '', role_id: 0, status: ADMIN_STATUS.ACTIVE as number };
const emptyRoleForm = { name: '', description: '', permissions: [] as string[] };

const RolePermission = () => {
  const { confirm, alert } = usePopup();
  const { admin: currentUser } = useAuth();

  const [tab, setTab] = useState<Tab>('users');
  const [modules, setModules] = useState<ModuleDef[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<{ text: string; ok: boolean } | null>(null);

  // Admin user create/edit modal
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [editingAdminId, setEditingAdminId] = useState<number | null>(null);
  const [adminForm, setAdminForm] = useState(emptyAdminForm);
  const [adminModalMsg, setAdminModalMsg] = useState('');

  // Role create modal
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [roleForm, setRoleForm] = useState(emptyRoleForm);
  const [roleModalMsg, setRoleModalMsg] = useState('');

  // Track per-role save state
  const [savingRoleId, setSavingRoleId] = useState<number | null>(null);

  // Password may only be changed for your own account (enforced on the backend too).
  const editingSelf = editingAdminId !== null && !!currentUser && currentUser.id === editingAdminId;

  const notify = (text: string, ok: boolean) => {
    setNotice({ text, ok });
    setTimeout(() => setNotice(null), 3500);
  };

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      api.get(API_ENDPOINTS.ADMIN.MODULES.GET_ALL),
      api.get(API_ENDPOINTS.ADMIN.ROLES.GET_ALL),
      api.get(API_ENDPOINTS.ADMIN.ADMIN_USERS.GET_ALL),
    ])
      .then(([modRes, roleRes, adminRes]) => {
        if (modRes.success && modRes.data) {
          const raw: any = modRes.data;
          setModules(Array.isArray(raw) ? raw : (raw.items || []));
        }
        if (roleRes.success && roleRes.data) {
          const raw: any = roleRes.data;
          setRoles(Array.isArray(raw) ? raw : (raw.items || []));
        }
        if (adminRes.success && adminRes.data) {
          const raw: any = adminRes.data;
          setAdmins(Array.isArray(raw) ? raw : (raw.items || []));
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load role & permission data:', err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // ---- Admin user actions ----
  const openCreateAdmin = () => {
    setEditingAdminId(null);
    setAdminForm({ ...emptyAdminForm, role_id: roles[0]?.id || 0 });
    setAdminModalMsg('');
    setAdminModalOpen(true);
  };

  const openEditAdmin = (admin: AdminUser) => {
    setEditingAdminId(admin.id);
    setAdminForm({ username: admin.username, email: admin.email, password: '', role_id: admin.role_id || 0, status: admin.status });
    setAdminModalMsg('');
    setAdminModalOpen(true);
  };

  const submitAdmin = (e: React.FormEvent) => {
    e.preventDefault();
    setAdminModalMsg('');

    if (!adminForm.role_id) {
      setAdminModalMsg('Please select a role for this admin user.');
      return;
    }

    if (editingAdminId === null) {
      // Create
      api.post(API_ENDPOINTS.ADMIN.ADMIN_USERS.CREATE, {
        username: adminForm.username,
        email: adminForm.email,
        password: adminForm.password,
        role_id: Number(adminForm.role_id),
        status: adminForm.status,
      })
        .then((res) => {
          if (res.success) {
            setAdminModalOpen(false);
            notify('Admin user created successfully.', true);
            fetchAll();
          } else {
            setAdminModalMsg(res.message || 'Failed to create admin user.');
          }
        })
        .catch(() => setAdminModalMsg('Error creating admin user.'));
    } else {
      // Update (only send password when provided)
      const payload: any = {
        email: adminForm.email,
        role_id: Number(adminForm.role_id),
        status: adminForm.status,
      };
      // Only the account owner may change their own password.
      if (adminForm.password && editingSelf) payload.password = adminForm.password;

      api.put(API_ENDPOINTS.ADMIN.ADMIN_USERS.UPDATE(editingAdminId), payload)
        .then((res) => {
          if (res.success) {
            setAdminModalOpen(false);
            notify('Admin user updated successfully.', true);
            fetchAll();
          } else {
            setAdminModalMsg(res.message || 'Failed to update admin user.');
          }
        })
        .catch(() => setAdminModalMsg('Error updating admin user.'));
    }
  };

  const reassignRole = (admin: AdminUser, roleId: number) => {
    api.put(API_ENDPOINTS.ADMIN.ADMIN_USERS.UPDATE(admin.id), { role_id: roleId })
      .then((res) => {
        if (res.success) {
          notify(`Role updated for ${admin.username}.`, true);
          fetchAll();
        } else {
          notify(res.message || 'Failed to update role.', false);
        }
      })
      .catch(() => notify('Error updating role.', false));
  };

  const toggleAdminStatus = (admin: AdminUser) => {
    const newStatus = admin.status === ADMIN_STATUS.ACTIVE ? ADMIN_STATUS.SUSPENDED : ADMIN_STATUS.ACTIVE;
    api.post(API_ENDPOINTS.ADMIN.ADMIN_USERS.UPDATE_STATUS(admin.id), { status: newStatus })
      .then((res) => {
        if (res.success) {
          notify(`${admin.username} ${newStatus === ADMIN_STATUS.ACTIVE ? 'activated' : 'suspended'}.`, true);
          fetchAll();
        } else {
          notify(res.message || 'Failed to update status.', false);
        }
      })
      .catch(() => notify('Error updating status.', false));
  };

  const deleteAdmin = async (admin: AdminUser) => {
    const ok = await confirm({
      title: 'Delete Admin User',
      message: `Permanently remove admin account "${admin.username}"? This cannot be undone.`,
      variant: 'danger',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
    });
    if (!ok) return;
    api.delete(API_ENDPOINTS.ADMIN.ADMIN_USERS.DELETE(admin.id))
      .then((res) => {
        if (res.success) {
          notify('Admin user deleted.', true);
          fetchAll();
        } else {
          notify(res.message || 'Failed to delete admin user.', false);
        }
      })
      .catch(() => notify('Error deleting admin user.', false));
  };

  // ---- Role actions ----
  const openCreateRole = () => {
    setRoleForm(emptyRoleForm);
    setRoleModalMsg('');
    setRoleModalOpen(true);
  };

  const toggleRoleFormPermission = (key: string) => {
    setRoleForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter((p) => p !== key)
        : [...f.permissions, key],
    }));
  };

  const submitRole = (e: React.FormEvent) => {
    e.preventDefault();
    setRoleModalMsg('');
    api.post(API_ENDPOINTS.ADMIN.ROLES.CREATE, {
      name: roleForm.name,
      description: roleForm.description,
      permissions: roleForm.permissions,
    })
      .then((res) => {
        if (res.success) {
          setRoleModalOpen(false);
          notify('Role created successfully.', true);
          fetchAll();
        } else {
          setRoleModalMsg(res.message || 'Failed to create role.');
        }
      })
      .catch(() => setRoleModalMsg('Error creating role.'));
  };

  // Toggle a module permission on an existing role (local draft until saved)
  const toggleRolePermission = (roleId: number, key: string) => {
    setRoles((prev) =>
      prev.map((r) => {
        if (r.id !== roleId) return r;
        const has = r.permissions.includes(key);
        return { ...r, permissions: has ? r.permissions.filter((p) => p !== key) : [...r.permissions, key] };
      })
    );
  };

  const saveRolePermissions = (role: Role) => {
    setSavingRoleId(role.id);
    api.put(API_ENDPOINTS.ADMIN.ROLES.UPDATE_PERMISSIONS(role.id), { permissions: role.permissions })
      .then((res) => {
        setSavingRoleId(null);
        if (res.success) {
          notify(`Permissions saved for ${role.name}.`, true);
        } else {
          notify(res.message || 'Failed to save permissions.', false);
        }
      })
      .catch(() => {
        setSavingRoleId(null);
        notify('Error saving permissions.', false);
      });
  };

  const deleteRole = async (role: Role) => {
    if (role.is_system) {
      alert({ title: 'Protected Role', message: `"${role.name}" is a built-in role and cannot be deleted. You can still edit its permissions.`, variant: 'warning' });
      return;
    }
    if (Number(role.user_count) > 0) {
      alert({ title: 'Role In Use', message: `"${role.name}" is assigned to ${role.user_count} admin user(s). Reassign them before deleting this role.`, variant: 'warning' });
      return;
    }
    const ok = await confirm({
      title: 'Delete Role',
      message: `Delete the role "${role.name}"? This cannot be undone.`,
      variant: 'danger',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
    });
    if (!ok) return;
    api.delete(API_ENDPOINTS.ADMIN.ROLES.DELETE(role.id))
      .then((res) => {
        if (res.success) {
          notify('Role deleted.', true);
          fetchAll();
        } else {
          notify(res.message || 'Failed to delete role.', false);
        }
      })
      .catch(() => notify('Error deleting role.', false));
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-muted font-medium">
        <RefreshCw className="animate-spin mr-2" size={20} /> Loading admin users & clearances...
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header Panel */}
      <div className="bg-card p-6 rounded-2xl border border-line flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-yellow-500/10 text-brand-gold rounded-xl">
            <ShieldCheck size={24} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-heading">Admin Users & RBAC</h3>
            <p className="text-sm text-muted">Manage staff accounts and edit the module clearances of each role.</p>
          </div>
        </div>
        <button
          onClick={tab === 'users' ? openCreateAdmin : openCreateRole}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-gold hover:bg-opacity-95 text-brand-dark font-bold rounded-xl transition-all shadow-sm text-sm"
        >
          {tab === 'users' ? <><UserPlus size={18} /> Add Admin User</> : <><Plus size={18} /> Add Role</>}
        </button>
      </div>

      {/* Notice banner */}
      {notice && (
        <div className={`px-4 py-3 rounded-xl text-sm font-semibold border flex items-center gap-2 ${
          notice.ok ? 'bg-green-500/10 text-green-700 border-green-200' : 'bg-red-500/10 text-brand-danger border-red-200'
        }`}>
          {notice.ok ? <Check size={16} /> : <X size={16} />} {notice.text}
        </div>
      )}

      {/* Tab switcher */}
      <div className="flex gap-2 border-b border-line">
        {([
          { key: 'users', label: 'Admin Users', icon: Users },
          { key: 'roles', label: 'Roles & Permissions', icon: Lock },
        ] as { key: Tab; label: string; icon: any }[]).map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-bold border-b-2 -mb-px transition-colors ${
              tab === key ? 'border-brand-gold text-heading' : 'border-transparent text-muted hover:text-body'
            }`}
          >
            <Icon size={16} /> {label}
            <span className="ml-1 text-xs bg-subtle text-muted px-2 py-0.5 rounded-full">
              {key === 'users' ? admins.length : roles.length}
            </span>
          </button>
        ))}
      </div>

      {/* ---- Admin Users tab ---- */}
      {tab === 'users' && (
        <div className="bg-card rounded-2xl shadow-sm border border-line overflow-hidden">
          <div className={`${LIST_TD_CLASS} border-b border-line flex items-center justify-between`}>
            <h4 className="font-bold text-heading">Administrative Accounts</h4>
            <span className="text-xs bg-subtle text-muted px-2.5 py-1 rounded-full font-semibold">
              {admins.length} Accounts
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className={LIST_TABLE_CLASS}>
              <thead className={LIST_THEAD_CLASS}>
                <tr>
                  <th className={`${LIST_TH_NO_CLASS} px-6 py-4`}>No.</th>
                  <th className={LIST_TH_CLASS}>Account</th>
                  <th className={LIST_TH_CLASS}>Email</th>
                  <th className={LIST_TH_CLASS}>Assigned Role</th>
                  <th className={LIST_TH_CLASS}>Status</th>
                  <th className={LIST_TH_CLASS}>Actions</th>
                </tr>
              </thead>
              <tbody className={LIST_TBODY_CLASS}>
                {admins.length > 0 ? (
                  admins.map((admin, index) => (
                    <tr key={admin.id} className="hover:bg-subtle/50 transition-colors">
                      <td className={`${LIST_TD_NO_CLASS} px-6 py-4`}>{listRowNumber(1, index, 0)}</td>
                      <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                        <div className="text-sm font-semibold text-heading">{admin.username}</div>
                        <div className="text-xs text-muted">ADM-{admin.id}</div>
                      </td>
                      <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm text-muted`}>{admin.email}</td>
                      <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm`}>
                        <select
                          value={admin.role_id || ''}
                          onChange={(e) => reassignRole(admin, Number(e.target.value))}
                          className="rounded-lg border-line text-xs font-bold text-heading bg-subtle hover:bg-subtle p-2 focus:ring-brand-gold focus:border-brand-gold border transition-colors cursor-pointer"
                        >
                          {roles.map((r) => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                          ))}
                        </select>
                      </td>
                      <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                        <button
                          onClick={() => toggleAdminStatus(admin)}
                          className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full border transition-colors ${
                            admin.status === ADMIN_STATUS.ACTIVE
                              ? 'bg-green-500/10 text-green-700 border-green-200 hover:bg-green-500/15'
                              : 'bg-red-500/10 text-red-700 border-red-200 hover:bg-red-500/15'
                          }`}
                          title="Toggle status"
                        >
                          {admin.status === ADMIN_STATUS.ACTIVE ? 'Authorized' : 'Suspended'}
                        </button>
                      </td>
                      <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm font-medium`}>
                        <div className="flex gap-3">
                          <button
                            onClick={() => openEditAdmin(admin)}
                            className="p-1 text-blue-600 hover:bg-blue-500/10 rounded"
                            title="Edit admin user"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => deleteAdmin(admin)}
                            className="p-1 text-brand-danger hover:bg-red-500/10 rounded"
                            title="Delete admin user"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-10 text-center text-muted font-medium">
                      No admin users yet. Click "Add Admin User" to provision staff access.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ---- Roles & Permissions tab ---- */}
      {tab === 'roles' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {roles.map((role) => (
            <div key={role.id} className="bg-card rounded-2xl shadow-sm border border-line overflow-hidden flex flex-col">
              <div className="p-6 border-b border-line flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="p-2.5 bg-amber-500/10 text-brand-gold rounded-xl shrink-0">
                    <Shield size={20} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-extrabold text-heading">{role.name}</h4>
                      {role.is_system && (
                        <span className="text-[10px] bg-subtle text-muted px-2 py-0.5 rounded-full font-bold uppercase tracking-wide flex items-center gap-1">
                          <Lock size={10} /> System
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted mt-1 max-w-sm">{role.description}</p>
                    <p className="text-xs text-muted mt-1 font-semibold">{role.user_count} admin user(s) · {role.permissions.length}/{modules.length} modules</p>
                  </div>
                </div>
                <button
                  onClick={() => deleteRole(role)}
                  disabled={role.is_system}
                  className={`p-1 rounded transition-colors ${
                    role.is_system ? 'text-faint cursor-not-allowed' : 'text-brand-danger hover:bg-red-500/10'
                  }`}
                  title={role.is_system ? 'System roles cannot be deleted' : 'Delete role'}
                >
                  <Trash2 size={16} />
                </button>
              </div>

              <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-2 flex-1">
                {modules.map((mod) => {
                  const checked = role.permissions.includes(mod.key);
                  return (
                    <label
                      key={mod.key}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition-colors text-sm ${
                        checked ? 'bg-brand-gold/10 border-brand-gold/40 text-heading' : 'bg-subtle border-line text-muted hover:bg-subtle'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleRolePermission(role.id, mod.key)}
                        className="rounded text-brand-gold focus:ring-brand-gold border-line"
                      />
                      <span className="font-semibold">{mod.name}</span>
                    </label>
                  );
                })}
              </div>

              <div className={`${LIST_TD_CLASS} bg-subtle border-t border-line flex justify-end`}>
                <button
                  onClick={() => saveRolePermissions(role)}
                  disabled={savingRoleId === role.id}
                  className="flex items-center gap-2 px-4 py-2 bg-brand-dark hover:bg-opacity-90 text-white rounded-lg font-bold text-sm transition-all shadow-sm disabled:opacity-60"
                >
                  <Save size={15} /> {savingRoleId === role.id ? 'Saving...' : 'Save Permissions'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---- Admin User create/edit modal ---- */}
      {adminModalOpen && (
        <div className="fixed inset-0 bg-brand-dark/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn">
          <div className="bg-card w-full max-w-md rounded-2xl shadow-xl overflow-hidden animate-scaleIn border border-line">
            <div className="p-6 border-b border-line flex items-center justify-between bg-gradient-to-r from-brand-dark to-slate-900 text-white">
              <div>
                <h4 className="font-bold text-lg text-brand-gold">{editingAdminId === null ? 'Add Admin User' : 'Edit Admin User'}</h4>
                <p className="text-xs text-faint">Provision a staff account and assign a role</p>
              </div>
              <button onClick={() => setAdminModalOpen(false)} className="text-muted hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={submitAdmin} className="p-6 space-y-4">
              {adminModalMsg && (
                <div className="px-4 py-2.5 rounded-xl text-xs font-semibold border bg-red-500/10 text-brand-danger border-red-200">
                  {adminModalMsg}
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Username</label>
                <input
                  type="text"
                  required
                  disabled={editingAdminId !== null}
                  placeholder="e.g. jsmith"
                  value={adminForm.username}
                  onChange={(e) => setAdminForm({ ...adminForm, username: e.target.value })}
                  className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border disabled:bg-subtle disabled:text-muted"
                />
                {editingAdminId !== null && <p className="text-[11px] text-muted mt-1">Username cannot be changed.</p>}
              </div>

              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Email</label>
                <input
                  type="email"
                  required
                  placeholder="staff@teenpatti.com"
                  value={adminForm.email}
                  onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                  className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border"
                />
              </div>

              {(editingAdminId === null || editingSelf) ? (
                <div>
                  <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2 flex items-center gap-1">
                    <KeyRound size={12} /> Password {editingSelf && <span className="text-faint normal-case font-medium">(leave blank to keep current)</span>}
                  </label>
                  <input
                    type="password"
                    required={editingAdminId === null}
                    minLength={6}
                    placeholder={editingAdminId === null ? 'Min. 6 characters' : '••••••••'}
                    value={adminForm.password}
                    onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                    className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border"
                  />
                </div>
              ) : (
                <div className="px-4 py-2.5 rounded-xl text-xs font-medium border bg-subtle text-muted border-line flex items-center gap-2">
                  <Lock size={14} /> Password can only be changed by the account owner.
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Role</label>
                  <select
                    value={adminForm.role_id}
                    onChange={(e) => setAdminForm({ ...adminForm, role_id: Number(e.target.value) })}
                    className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border"
                  >
                    <option value={0} disabled>Select role…</option>
                    {roles.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Status</label>
                  <select
                    value={adminForm.status}
                    onChange={(e) => setAdminForm({ ...adminForm, status: Number(e.target.value) })}
                    className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border"
                  >
                    <option value={ADMIN_STATUS.ACTIVE}>Active</option>
                    <option value={ADMIN_STATUS.SUSPENDED}>Suspended</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setAdminModalOpen(false)}
                  className="flex-1 px-4 py-2.5 bg-subtle hover:bg-subtle text-body rounded-lg font-semibold border border-line transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 bg-brand-gold hover:bg-opacity-95 text-brand-dark rounded-lg font-bold transition-all text-sm shadow-sm"
                >
                  {editingAdminId === null ? 'Create Account' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---- Role create modal ---- */}
      {roleModalOpen && (
        <div className="fixed inset-0 bg-brand-dark/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn">
          <div className="bg-card w-full max-w-lg rounded-2xl shadow-xl overflow-hidden animate-scaleIn border border-line">
            <div className="p-6 border-b border-line flex items-center justify-between bg-gradient-to-r from-brand-dark to-slate-900 text-white">
              <div>
                <h4 className="font-bold text-lg text-brand-gold">Add Role</h4>
                <p className="text-xs text-faint">Define a custom role and its module clearances</p>
              </div>
              <button onClick={() => setRoleModalOpen(false)} className="text-muted hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={submitRole} className="p-6 space-y-4">
              {roleModalMsg && (
                <div className="px-4 py-2.5 rounded-xl text-xs font-semibold border bg-red-500/10 text-brand-danger border-red-200">
                  {roleModalMsg}
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Role Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Finance Auditor"
                  value={roleForm.name}
                  onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                  className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Description</label>
                <input
                  type="text"
                  placeholder="What this role is responsible for"
                  value={roleForm.description}
                  onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                  className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Module Clearances</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1">
                  {modules.map((mod) => {
                    const checked = roleForm.permissions.includes(mod.key);
                    return (
                      <label
                        key={mod.key}
                        className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition-colors text-sm ${
                          checked ? 'bg-brand-gold/10 border-brand-gold/40 text-heading' : 'bg-subtle border-line text-muted hover:bg-subtle'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleRoleFormPermission(mod.key)}
                          className="rounded text-brand-gold focus:ring-brand-gold border-line"
                        />
                        <span className="font-semibold">{mod.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-4 pt-2">
                <button
                  type="button"
                  onClick={() => setRoleModalOpen(false)}
                  className="flex-1 px-4 py-2.5 bg-subtle hover:bg-subtle text-body rounded-lg font-semibold border border-line transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 bg-brand-gold hover:bg-opacity-95 text-brand-dark rounded-lg font-bold transition-all text-sm shadow-sm"
                >
                  Create Role
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolePermission;
